"use client";

import {
  Box,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  Button,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search as SearchIcon } from "lucide-react";
import { ChangeEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import EventCard from "./EventCard";
import { useListStageCards } from "../../../../../services/stagemode_api/talent/talent_api";

/* --------------------------------- types --------------------------------- */
interface FeaturedEventsSectionProps {
  fromRoutes?: boolean;
}

const MAX_VISIBLE = 20;

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
};

/* -------------------------------- component ------------------------------- */
const FeaturedEventsSection: React.FC<FeaturedEventsSectionProps> = ({
  fromRoutes = false,
}) => {
  const { data, isLoading } = useListStageCards();
  const events = data?.data ?? [];

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* ------------------------------ search state ----------------------------- */
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  /* -------------------------- filtered + paginated ------------------------- */
  const filteredEvents = useMemo(
    () =>
      events.filter((e: any) =>
        e.title?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      ),
    [events, searchTerm]
  );

  const [showAll, setShowAll] = useState(false);
  const visibleEvents = showAll
    ? filteredEvents
    : filteredEvents.slice(0, MAX_VISIBLE);

  /* ----------------------------- empty state ------------------------------ */
  if (!events.length && !isLoading) {
    return (
      <Box
        px={{ xs: 2, sm: 3, md: 4 }}
        py={6}
        textAlign="center"
        sx={{ color: COLORS.text }}
      >
        <Typography
          variant="h6"
          fontFamily="Lexend"
          sx={{ color: COLORS.text }}
        >
          No Featured Events
        </Typography>
        <Typography
          fontSize={14}
          fontFamily="Lexend"
          sx={{ color: COLORS.muted }}
        >
          Check back later for exciting events curated just for you.
        </Typography>
      </Box>
    );
  }

  /* ------------------------------ component UI ----------------------------- */
  return (
    <Box
      px={{ xs: 2, sm: 3, md: 6 }}
      py={{ xs: 5, sm: 6 }}
      sx={{
        bgcolor: COLORS.panel,
        borderRadius: 3,
        color: COLORS.text,
        border: `1px solid ${COLORS.border}`,
      }}
    >
      {/* Header + Search */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        mb={fromRoutes ? 4 : 3}
        spacing={2}
      >
        {!fromRoutes && (
          <Typography
            fontFamily="Lexend"
            fontWeight={700}
            fontSize={{ xs: 20, sm: 24, md: 26 }}
            sx={{ color: COLORS.text }}
          >
            🌟 Stage Events
          </Typography>
        )}

        {/* Search bar is **always** rendered */}
        <TextField
          placeholder="Search by title…"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
          sx={{
            width: { xs: "100%", sm: 280, md: 320 },
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.06)",
            boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: COLORS.muted,
              opacity: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={18} style={{ color: COLORS.muted }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {!fromRoutes && (
        <Divider
          sx={{
            borderColor: COLORS.border,
            mb: 3,
            display: { xs: "none", sm: "block" },
          }}
        />
      )}

      {/* Grid */}
      <Grid container spacing={3}>
        {visibleEvents.map((event: any) => (
          <Grid item key={event.id} xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Box
              onClick={() => navigate(`/featured-event/${event.id}`)}
              sx={{
                cursor: "pointer",
                transition: "transform .25s",
                "&:hover": { transform: "translateY(-4px)" },
                height: "100%",
              }}
            >
              <EventCard event={event} />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* View-more toggle */}
      {filteredEvents.length > MAX_VISIBLE && (
        <Box mt={6} textAlign="center">
          <Button
            onClick={() => setShowAll((prev) => !prev)}
            variant="contained"
            sx={{
              fontFamily: "Lexend",
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(to right, #e50914, #b81d24)",
              color: COLORS.text,
              "&:hover": {
                background: "linear-gradient(to right, #b20710, #8b1519)",
              },
            }}
          >
            {showAll ? "Show Less" : `View All (${filteredEvents.length})`}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FeaturedEventsSection;
